/**
 * CaseMonkey PDP enhancements
 *
 * Handles:
 *   1. URL-based variant pre-select (for Google Shopping ads with ?model=X&colour=Y)
 *   2. Fit confirmation panel updater (keeps "Confirmed fit: [model]" in sync)
 *   3. Sticky mobile buy bar (appears when main CTA scrolls out of view)
 *   4. Related product async add-to-cart (Pattern 5 upsell — no page reload)
 *
 * Loaded only on product templates via theme.liquid.
 */

(function() {
  'use strict';

  // ============ 1. URL VARIANT PRE-SELECT ============
  function preSelectVariant() {
    if (!document.querySelector('.template-suffix-casemonkey-v2')) return;
    var params = new URLSearchParams(window.location.search);
    if (params.has('variant')) return;

    var targetModel = params.get('model');
    var targetColour = params.get('colour') || params.get('color');
    if (!targetModel && !targetColour) return;

    var optionInputs = document.querySelectorAll(
      '[name^="options["], select[name*="options"]'
    );

    optionInputs.forEach(function(input) {
      var match = input.name.match(/options\[(.+?)\]/);
      var optionName = match ? match[1].toLowerCase() : '';

      var target = null;
      if ((optionName.indexOf('phone') !== -1 || optionName.indexOf('model') !== -1) && targetModel) {
        target = targetModel;
      } else if (optionName.indexOf('colour') !== -1 || optionName.indexOf('color') !== -1) {
        target = targetColour;
      }
      if (!target) return;

      if (input.type === 'radio') {
        if (input.value.toLowerCase() === target.toLowerCase()) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else if (input.tagName === 'SELECT') {
        var opt = Array.from(input.options).find(function(o) {
          return o.text.toLowerCase() === target.toLowerCase();
        });
        if (opt) {
          input.value = opt.value;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  // ============ 2. FIT CONFIRMATION UPDATER ============
  function updateFitConfirmation() {
    var panel = document.querySelector('[data-fit-confirmation]');
    if (!panel) return;

    var modelEl = panel.querySelector('[data-fit-model]');
    if (!modelEl) return;

    var modelIndex = parseInt(panel.getAttribute('data-fit-model-index'), 10);

    // If URL has ?variant=, confirm immediately on load
    if (window.location.search.indexOf('variant=') !== -1) {
      panel.classList.add('has-confirmed');
    }

    document.addEventListener('variant:change', function(e) {
      var variant = e.detail && e.detail.variant;
      if (!variant) return;
      panel.classList.add('has-confirmed');
      if (modelIndex >= 0 && variant.options && variant.options[modelIndex]) {
        modelEl.textContent = variant.options[modelIndex];
      }
    });
  }

  // ============ 3. STICKY BUY BAR ============
  function initStickyBar() {
    var bar = document.querySelector('[data-sticky-bar]');
    var mainBtn = document.querySelector(
      'form[action*="/cart/add"] [type="submit"], product-form button[type="submit"]'
    );
    if (!bar || !mainBtn) return;

    var variantEl = bar.querySelector('[data-sticky-variant]');
    var priceEl = bar.querySelector('[data-sticky-price]');
    var stickyBtn = bar.querySelector('[data-sticky-add]');

    document.addEventListener('variant:change', function(e) {
      var variant = e.detail && e.detail.variant;
      if (!variant) return;
      if (variantEl) variantEl.textContent = variant.title;
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (stickyBtn) {
        stickyBtn.disabled = !variant.available;
        stickyBtn.textContent = variant.available ? 'Add to basket' : 'Sold out';
      }
    });

    if (stickyBtn) {
      stickyBtn.addEventListener('click', function() {
        mainBtn.click();
        mainBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        bar.classList.toggle('is-visible', !entry.isIntersecting);
      });
    }, { threshold: 0 });
    observer.observe(mainBtn);
  }

  // ============ 4. RELATED PRODUCT ASYNC ADD ============
  // Adds the related product (e.g. screen protector) to cart without
  // leaving the PDP, so the buyer can still complete their main case purchase.
  function initRelatedProductAdd() {
    var form = document.querySelector('[data-related-add-form]');
    if (!form) return;

    var btn = form.querySelector('[data-related-add-btn]');
    var label = btn ? btn.querySelector('[data-btn-label]') : null;
    var icon = btn ? btn.querySelector('.pdp-related__btn-icon') : null;

    form.addEventListener('submit', function(e) {
      e.preventDefault();
      if (!btn || btn.disabled) return;

      // Optimistic UI: show pending state
      var originalLabel = label ? label.textContent : 'Add';
      if (label) label.textContent = 'Adding...';
      btn.disabled = true;

      var formData = new FormData(form);

      fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' }
      })
      .then(function(response) {
        if (!response.ok) throw new Error('Add failed');
        return response.json();
      })
      .then(function(data) {
        // Success state
        if (label) label.textContent = 'Added';
        if (icon) icon.style.display = 'inline-block';

        // Update cart count if your theme exposes one
        updateCartCount();

        // Fire native cart-update event so other theme components refresh
        document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      })
      .catch(function() {
        // Failure state — revert
        if (label) label.textContent = 'Try again';
        btn.disabled = false;
        setTimeout(function() {
          if (label) label.textContent = originalLabel;
        }, 2000);
      });
    });
  }

  function updateCartCount() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        // Update common cart count elements — adjust selectors to match your theme
        document.querySelectorAll(
          '[data-cart-count], .cart-count-bubble span, .cart-count'
        ).forEach(function(el) {
          el.textContent = cart.item_count;
        });
      })
      .catch(function() {});
  }

  function formatMoney(cents) {
    return '£' + (cents / 100).toFixed(2);
  }

  // ============ 5. DESCRIPTION READ MORE / SHOW LESS ============
  function initDescriptionToggle() {
    if (!document.querySelector('.template-suffix-casemonkey-v2')) return;
    document.querySelectorAll('[data-description]').forEach(function(wrapper) {
      var toggle = wrapper.querySelector('[data-description-toggle]');
      var content = wrapper.querySelector('[data-description-content]');
      if (!toggle || !content) return;

      requestAnimationFrame(function() {
        if (content.scrollHeight <= content.clientHeight + 4) {
          toggle.style.display = 'none';
        }
      });

      toggle.addEventListener('click', function() {
        var expanded = wrapper.classList.toggle('is-expanded');
        toggle.setAttribute('aria-expanded', expanded);
        toggle.textContent = expanded ? 'Show less' : 'Read more';
      });
    });
  }

  // ============ 6. PROTECTOR UPSELL ============
  // Resolve a case's selected model-option value to a protector variant id.
  // The iPhone snippet emits EXACT keys ("iPhone 15"); the Samsung snippet emits
  // normalised "core" keys (lower-case, no spaces, leading "samsung" removed —
  // e.g. "s24ultra"). Try the exact key first so iPhone behaviour is unchanged,
  // then fall back to the normalised core so the Samsung map resolves regardless
  // of case, spacing ("Samsung S22Ultra") or the "Samsung " prefix.
  function normaliseModelKey(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/samsung/g, '');
  }

  function lookupProtectorVariant(map, value) {
    if (value == null) return null;
    if (Object.prototype.hasOwnProperty.call(map, value)) return map[value];
    var core = normaliseModelKey(value);
    if (core && Object.prototype.hasOwnProperty.call(map, core)) return map[core];
    return null;
  }

  function initProtectorUpsell() {
    var protectorUpsell = document.querySelector('[data-protector-upsell]');
    var protectorCheckbox = document.querySelector('[data-protector-checkbox]');

    if (!protectorUpsell || !protectorCheckbox) return;

    var variantsMap = {};
    try {
      variantsMap = JSON.parse(protectorUpsell.getAttribute('data-protector-variants') || '{}');
    } catch (err) {
      console.warn('Protector variants JSON parse failed', err);
    }

    document.addEventListener('variant:change', function(e) {
      var newVariant = e.detail && e.detail.variant;
      if (!newVariant) return;

      var options = newVariant.options || [newVariant.option1, newVariant.option2, newVariant.option3].filter(Boolean);
      var matchedVariantId = null;

      for (var i = 0; i < options.length; i++) {
        var found = lookupProtectorVariant(variantsMap, options[i]);
        if (found != null) {
          matchedVariantId = found;
          break;
        }
      }

      if (matchedVariantId != null) {
        // Selected model is covered — reveal the upsell and target the right pack.
        protectorCheckbox.setAttribute('data-variant-id', matchedVariantId);
        protectorUpsell.style.display = '';
      } else {
        // Selected model has no matching protector — hide and clear, so we never
        // add a stale/wrong variant to the cart.
        protectorUpsell.style.display = 'none';
        protectorCheckbox.checked = false;
        protectorCheckbox.setAttribute('data-variant-id', '');
      }
    });

    var addBtn = document.querySelector('.template-suffix-casemonkey-v2 [data-add-to-cart]');
    if (addBtn) {
      addBtn.addEventListener('click', function(e) {
        if (!protectorCheckbox.checked) return;

        var protectorVariantId = protectorCheckbox.getAttribute('data-variant-id');
        if (!protectorVariantId) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: parseInt(protectorVariantId, 10), quantity: 1 })
        })
        .then(function(response) { return response.json(); })
        .then(function() {
          protectorCheckbox.checked = false;
          $(addBtn).closest('form').trigger('submit');
        })
        .catch(function(err) {
          console.error('Protector add failed', err);
          protectorCheckbox.checked = false;
          $(addBtn).closest('form').trigger('submit');
        });
      }, true);
    }
  }

  // ============ INIT ============
  function init() {
    preSelectVariant();
    updateFitConfirmation();
    initStickyBar();
    initRelatedProductAdd();
    initDescriptionToggle();
    initProtectorUpsell();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
